import { useEffect } from 'react';
import type { DashboardDto } from '@shared/types';
import { saveBlob } from '@shared/api';
import { localDate, localDateTime, money, timeAgo, titleCase } from '@shared/format';
import { HubEvents } from '@shared/realtime';
import { errorMessage, useApp, useLoad } from '../context';
import { navigate } from '../router';
import { Card, Empty, ErrorBox, Loading, Pill, TopBar } from '../components/ui';

const DISPLAY_ID = import.meta.env.VITE_DISPLAY_ID || 'shop-main';

export function Dashboard() {
  const { api, hub, toast } = useApp();
  const { data, error, loading, reload } = useLoad(() => api.get<DashboardDto>('/api/dashboard'), [api]);

  useEffect(() => {
    if (!hub) return;
    const r = () => reload();
    const events = [HubEvents.DisplayStatusChanged, HubEvents.DrawingUpdated, HubEvents.WinnerRevealed, HubEvents.ReturnToLeaderboard];
    events.forEach((e) => hub.on(e, r));
    const t = setInterval(r, 30_000);
    return () => { events.forEach((e) => hub.off(e, r)); clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hub]);

  const exportCsv = async () => {
    try { const f = await api.download('/api/reports/winners.csv'); saveBlob(f.blob, f.filename); toast('Winners CSV downloaded'); }
    catch (e) { toast(errorMessage(e), 'error'); }
  };
  const returnToBoard = async () => {
    try { await api.post(`/api/displays/${DISPLAY_ID}/return-to-leaderboard`); toast('Display returned to leaderboard'); reload(); }
    catch (e) { toast(errorMessage(e), 'error'); }
  };

  const display = data?.displays.find((d) => d.displayId === DISPLAY_ID) ?? data?.displays[0];
  const open = data?.openDrawings ?? [];

  return (
    <div className="page">
      <TopBar title="Admin Dashboard" right={<img src="./brand/kes-logo-reversed.png" alt="KES" className="topbar-logo" />} />
      {loading && !data && <Loading />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <>
          <Card className="display-card">
            <div className="row-between">
              <div>
                <div className="card-kicker">Shop Display</div>
                <div className="display-status">
                  {display?.online ? <Pill kind="ok">Online</Pill> : <Pill kind="bad">Offline</Pill>}
                  <span className="muted">Mode: {display?.currentMode ?? '—'}</span>
                </div>
                <div className="muted small">Last seen: {display?.lastHeartbeatAtUtc ? `${localDateTime(display.lastHeartbeatAtUtc)} (${timeAgo(display.lastHeartbeatAtUtc)})` : 'never'}</div>
                {display?.lastError && <div className="warn small">Last display error: {display.lastError}</div>}
              </div>
              <button className="btn btn-secondary btn-sm" onClick={reload}>Refresh</button>
            </div>
            {display && display.currentMode !== 'Leaderboard' && (
              <button className="btn btn-secondary btn-block mt" onClick={returnToBoard}>Return display to leaderboard</button>
            )}
          </Card>

          <Card className="award-card">
            <div className="row-between">
              <div>
                <div className="card-kicker">Saw something good?</div>
                <div className="muted small">Give someone points for safe work you just observed.</div>
              </div>
              <button className="btn btn-go btn-sm" onClick={() => navigate('/award')}>Award Points</button>
            </div>
          </Card>

          {open.length > 0 && (
            <Card>
              <div className="card-kicker">In progress</div>
              {open.map((d) => (
                <button key={d.drawingId} className="list-row" onClick={() => navigate(d.status === 'Started' ? `/drawings/${d.drawingId}/live` : `/drawings/${d.drawingId}`)}>
                  <div>
                    <strong>{titleCase(d.drawingType)} drawing</strong> <Pill kind={d.status === 'Started' ? 'info' : d.status === 'Staged' ? 'warn' : 'muted'}>{d.status}</Pill>
                    <div className="muted small">{d.prizeName}{d.prizeValue > 0 ? ` • ${money(d.prizeValue)}` : ''} • {d.entries.length} entrants</div>
                  </div>
                  <span className="chev">›</span>
                </button>
              ))}
            </Card>
          )}

          {data.drawingTypes.map((t) => (
            <Card key={t.drawingTypeId} className="type-card">
              <div className="type-ico" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
              </div>
              <div className="type-body">
                <h2>{t.name} Drawing</h2>
                <div className="muted small">{t.periodLabel}</div>
                <div className="type-count"><strong className="tabular">{t.qualifiedCount}</strong> qualified employee{t.qualifiedCount === 1 ? '' : 's'} <span className="muted">(≥ {t.threshold} point{t.threshold === 1 ? '' : 's'})</span></div>
                <button className="btn btn-primary btn-block" onClick={() => navigate(`/draw/${t.name.toLowerCase()}`)}>Prepare Drawing</button>
              </div>
            </Card>
          ))}

          <Card>
            <div className="row-between">
              <div className="card-kicker">Recent winners</div>
              <button className="btn btn-primary btn-sm" onClick={exportCsv}>Export Winners CSV</button>
            </div>
            {data.recentWinners.length === 0 ? <Empty>No completed drawings yet.</Empty> : data.recentWinners.map((w) => (
              <div key={w.drawingId} className="winner-line">
                <div><strong>{w.winnerName}</strong><div className="muted small">{titleCase(w.drawingType)} • {localDate(w.completedAtUtc)}</div></div>
                <div className="right"><div>{w.prizeName}</div><div className="muted small">{money(w.prizeValue)}</div></div>
              </div>
            ))}
          </Card>

          <Card>
            <div className="row-between">
              <div className="card-kicker">Connecteam</div>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/integration')}>Details</button>
            </div>
            <div className="kv"><span>Status</span><span>{data.integration.enabled ? <Pill kind="ok">Enabled</Pill> : <Pill kind="muted">Not connected</Pill>}</span></div>
            <div className="kv"><span>Last employee sync</span><span>{timeAgo(data.integration.lastEmployeeSyncUtc)}</span></div>
            <div className="kv"><span>Last reconciliation</span><span>{timeAgo(data.integration.lastReconcileUtc)}</span></div>
            {(data.integration.unknownEmployeeEvents > 0 || data.integration.failedEvents > 0) && (
              <div className="warn small mt">{data.integration.unknownEmployeeEvents} held (unknown employee), {data.integration.failedEvents} failed</div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
